import { useState, useCallback, useEffect } from 'react'
import { fetchObjectInstances, fetchObjectInstance } from '../../../utils/decoupled'
import { decoupledDelete } from '../../../services/api'
import { parseMetaBlob } from '../normalizePage'

/**
 * Hook pour lister les instances d'un type d'objet (`builder_object_instance`).
 *
 * Source : la collection decoupled des `builder_unit` du type, servie par le
 * display `decoupled_list` → [{nid, slug, label}, ...].
 *
 * Le listing ne charge volontairement pas la `data` complète des instances
 * (chargement plus rapide pour une liste de N items). Pour éditer une
 * instance, utiliser `useBuilderObject(type, slugOrId)` qui ramène le
 * payload enrichi.
 */
export const useBuilderObjects = (type) => {
  const [objects, setObjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!type) {
      setObjects([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      setObjects(await fetchObjectInstances(type))
    } catch (e) {
      setError(e)
      setObjects([])
    } finally {
      setLoading(false)
    }
  }, [type])

  useEffect(() => { load() }, [load])

  /**
   * Supprime une instance par nid. Met à jour la liste locale.
   */
  const deleteObject = useCallback(async (nid) => {
    const data = await decoupledDelete('node', 'builder_unit', nid)
    setObjects(prev => prev.filter(o => o.nid !== nid))
    return data
  }, [])

  return { objects, loading, error, deleteObject, reload: load }
}

/**
 * Hook pour charger ET sauver une instance unique.
 *
 *  - la collection decoupled des `builder_unit`, résolue par slug ou par nid
 *  - POST /api/builder/object/save  (create si `nid` absent, update sinon)
 *
 * Le GET retourne le payload décodé via decoupled_toolbox — on extrait la
 * partie `data` (champ `objectData.data`) pour faciliter l'édition côté
 * formulaire.
 *
 * `slugOrId` peut être `null` pour démarrer en mode création (rien à
 * charger ; le formulaire part de `defaultData` du def).
 */
export const useBuilderObject = (type, slugOrId) => {
  const [object, setObject] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    if (!type || !slugOrId) {
      setObject(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const wrapped = await fetchObjectInstance(type, slugOrId)
      // L'endpoint utilise le rendu décodé wrappé Syncable. C1 : l'objet est un
      // `builder_unit`, dont la donnée est émise sous `objectData` (clé unifiée) :
      //   { data: { id, name, type, slug, objectData: { data: {...} } }, ... }
      const inner = wrapped?.data ?? wrapped
      const unitData = inner?.objectData ?? { data: {} }
      setObject({
        nid: inner?.id,
        type: inner?.type,
        slug: inner?.slug || '',
        label: inner?.name || '',
        data: unitData?.data ?? {},
        // La vie PUBLIQUE du contenu, servie par le modèle de son type : ses
        // balises `<head>` (blob JSON, même champ et même panneau qu'une page)
        // et son état de publication. Ce dernier arrive sous `published` et non
        // `status` : `status` est un champ de BASE, qu'aucun view display ne
        // sait exposer, et la clé est réservée par l'enveloppe Syncable — c'est
        // `UnitOutputSubscriber` qui le pose (même nom que sur une page).
        // Absent ⇒ publié, comme le défaut Drupal.
        meta: parseMetaBlob(inner?.meta),
        published: inner?.published !== false,
      })
    } catch (e) {
      setError(e)
      setObject(null)
    } finally {
      setLoading(false)
    }
  }, [type, slugOrId])

  useEffect(() => { load() }, [load])

  /**
   * Crée ou met à jour l'instance. Retourne le payload réponse
   * (`{nid, type, slug, label, data}`).
   *
   * `siteId` : le site où faire NAÎTRE un contenu — celui de son type. Le back
   * le prend par défaut s'il est absent ; le passer explicitement dit
   * l'intention. Sans effet sur une mise à jour (le contenu garde son site).
   *
   * `meta` / `published` suivent le CONTRAT DE PATCH du back : une clé absente
   * laisse la valeur en base. Un appelant qui ne s'occupe que de la donnée
   * (l'assistant, un script d'import) n'efface donc ni les balises de partage
   * ni l'état de publication.
   */
  const saveObject = useCallback(async ({ nid, slug, data, siteId = null, meta = null, published = null}) => {
    const res = await fetch('/api/builder/object/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        nid,
        type,
        slug,
        data,
        ...(siteId ? { siteId } : {}),
        ...(meta ? { meta } : {}),
        ...(published === null ? {} : { published }),
      }),
    })
    const body = await res.json()
    if (!res.ok || !body.success) {
      throw new Error(body.message || `Save error: ${res.status}`)
    }
    // Met à jour l'état local pour refléter le slug/nid potentiellement
    // ajustés par le serveur (unicité, fallback…).
    setObject({
      nid: body.nid,
      type: body.type,
      slug: body.slug || '',
      label: body.label || '',
      data: body.data ?? {},
      meta: body.meta ?? {},
      published: body.published !== false,
    })
    return body
  }, [type])

  return { object, loading, error, saveObject, reload: load }
}
